import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useMemo } from 'react';
import { useUser } from 'shared/hooks/use-user';
import { Goal } from 'shared/types/goal';
import { goalInboxID } from 'shared/types/orderable-section';
import { GoalGroupBy } from 'shared/types/sorting-mode';
import { getSubGoals } from 'shared/utils/get-sub-goals';
import { sortGoals } from 'shared/utils/sort-goals';
import { sortItems } from 'shared/utils/sort-items';

export const useGoalSections = () => {
  const user = useUser();

  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const goalOrder = useMemo(() => {
    const mainGoals = goals.filter(({ parentIds }) => !parentIds?.length);
    const subGoals = goals.filter(({ parentIds }) => !!parentIds?.length);
    // default sort the goals on roadmap inProgress sorting
    let sortedMainGoals = sortGoals(mainGoals, {
      allGoals: goals,
      sortMode: user?.settings?.goalSortingMode,
      customOrder: user?.settings?.goalRoadmapSorting?.inProgress,
    });

    if (user?.settings?.goalGroupBy === GoalGroupBy.LifeArea) {
      // sort life-areas
      const sortedLifeAreas = sortItems(
        lifeAreas,
        user?.settings?.lifeAreaSorting ?? [],
        'id',
      );
      // group goals on life-area
      sortedMainGoals = [
        ...sortedLifeAreas
          .map(({ id }) =>
            sortGoals(
              mainGoals.filter(({ lifeAreaId }) => lifeAreaId === id),
              {
                allGoals: goals,
                sortMode: user?.settings?.goalSortingMode,
                customOrder:
                  user?.settings?.goalGroupSorting?.lifeArea?.sorting?.[id] ??
                  [],
              },
            ),
          )
          .flat(1),
        ...sortGoals(
          mainGoals.filter(({ lifeAreaId }) => !lifeAreaId),
          {
            allGoals: goals,
            sortMode: user?.settings?.goalSortingMode,
            customOrder:
              user?.settings?.goalGroupSorting?.lifeArea?.sorting?.null ?? [],
          },
        ),
      ];
    }

    // Helper function to recursively sort and add sub-goals
    const addSortedSubGoals = (
      parentGoal: Goal,
      allSubGoals: Goal[],
    ): Goal[] => {
      const directSubGoals = getSubGoals(parentGoal.id, { goals: allSubGoals });

      if (!directSubGoals.length) {
        return [];
      }

      const sortedDirectSubGoals = sortGoals(directSubGoals, {
        allGoals: goals,
        sortMode: parentGoal.goalSortingMode,
        customOrder: parentGoal.goalSorting,
      });

      const result: Goal[] = [];

      // Add each direct sub-goal and its sub-goals recursively
      for (const subGoal of sortedDirectSubGoals) {
        result.push(subGoal);
        result.push(...addSortedSubGoals(subGoal, allSubGoals));
      }

      return result;
    };

    return sortedMainGoals
      .reduce<Goal[]>((acc, goal) => {
        acc.push(goal);
        acc.push(...addSortedSubGoals(goal, subGoals));
        return acc;
      }, [])
      .map(({ id }) => id);
  }, [
    goals,
    lifeAreas,
    user?.settings?.goalGroupBy,
    user?.settings?.goalGroupSorting?.lifeArea?.sorting,
    user?.settings?.goalRoadmapSorting?.inProgress,
    user?.settings?.goalSortingMode,
    user?.settings?.lifeAreaSorting,
  ]);

  return useMemo(
    () => [
      // sort goals before mapping to sections
      ...sortItems(
        goals,
        goalOrder,
        'id',
        // map to sections
      ).map(({ id }) => ({
        id,
        items: [],
      })),
      // add inbox as group as well
      {
        id: goalInboxID,
        items: [],
      },
      // filter out the empty sections, except inbox
    ],
    [goalOrder, goals],
  );
};
